import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Tag, Typography, Upload } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';

import DragUploadFileListItem from './DragUploadFileListItem';
import ChunkUploader, { IOnProgressOptions } from '../utils/chunkUploader';
import { IFile } from '../types/file';
import FormLabel from './FormLabel';
import { useDownloadFile } from '../hooks/useDownloadFile';
import { useInteractDownloadFile } from '../hooks/useInteractDownloadFile';
import { useMessage } from '../hooks/useMessage';

interface IProps {
  initializeUploadURL: string;
  finalizeUploadURL: string;
  required?: boolean;
  label?: string;
  mode: ChunkFileUploadMode;
  accept?: string;
  placeholder?: string;
  initFile?: IFile;
  chunkSize?: number;
  retryTooltip?: string;
  downloadTooltip?: string;
  deleteTooltip?: string;
  onComplete?: (file: IFile) => void;
  onDelete?: (fileId: string) => void;
}

export enum ChunkFileUploadMode {
  Upload = 'upload',
  View = 'view',
}

export enum UploadFileStatus {
  Done = 'done',
  Success = 'success',
  Error = 'error',
  Uploading = 'uploading',
}

export interface IUploadingItem {
  localId: string;
  name: string;
  id?: string;
  status: UploadFileStatus;
  path?: string;
}

const ChunkFileUpload: React.FC<IProps> = ({
  initializeUploadURL,
  finalizeUploadURL,
  required,
  label,
  mode,
  accept,
  placeholder,
  initFile,
  chunkSize,
  onComplete,
  onDelete,
  retryTooltip,
  downloadTooltip,
  deleteTooltip,
}) => {
  const message = useMessage();

  const [progress, setProgress] = useState<IOnProgressOptions | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { downloadFile: downloadCoreFile } = useDownloadFile();

  const { downloadFile: downloadInteractFile } = useInteractDownloadFile();

  const uploader = useRef(
    new ChunkUploader({ initializeUploadURL, finalizeUploadURL, chunkSize })
  ).current;

  const [uploadingData, setUploadingData] = useState<IUploadingItem | null>(
    initFile && Object.keys(initFile).length > 0
      ? {
          id: initFile.id,
          name: initFile.name,
          localId: initFile.id,
          status: UploadFileStatus.Done,
          path: initFile.path,
        }
      : null
  );

  useEffect(() => {
    if (initFile && initFile.id && !uploadingData) {
      setUploadingData({
        id: initFile.id,
        name: initFile.name,
        localId: initFile.id,
        status: UploadFileStatus.Done,
        path: initFile.path,
      });
    }
  }, [initFile]);

  useEffect(() => {
    if (file) {
      uploader
        .onProgress(data => {
          // to avoid the same percentage to be logged twice
          if (data.percentage !== progress?.percentage) {
            setProgress(data);
          }
        })
        .onError(() => {
          setUploadingData((data: IUploadingItem | null) => {
            if (!data) return null;
            return {
              ...data,
              status: UploadFileStatus.Error,
            };
          });
        })
        .onComplete((uploadedData: IFile) => {
          onComplete?.(uploadedData);
          setUploadingData({
            localId: uploadedData.id,
            name: uploadedData.name,
            status: UploadFileStatus.Success,
            id: uploadedData.id,
            path: uploadedData.path,
          });
        })
        .start(file);

      setUploadingData({
        localId: '1',
        name: file.name,
        status: UploadFileStatus.Uploading,
        id: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const handleDeleteLocalFile = () => {
    uploader.stop();
    setFile(null);
    setUploadingData(null);
    setProgress(null);
  };

  const handleDeleteFile = (file: IUploadingItem) => {
    if (file.id) {
      onDelete?.(file.id);
    }
    handleDeleteLocalFile();
  };

  const handleRetry = () => {
    setUploadingData((data: IUploadingItem | null) => {
      if (!data) return null;
      return { ...data, status: UploadFileStatus.Uploading };
    });
    uploader.retry();
  };

  const handleDownload = (file: IUploadingItem) => {
    const path = file?.path;
    const fileId = file?.id;

    if (!path || !fileId) {
      message.error('File not found');
      return;
    }

    if (path.includes('uploads/interact')) {
      downloadInteractFile({ fileId });
      return;
    }

    if (path.includes('model-instances')) {
      const modelInstanceId = path.split('/')[1];
      downloadCoreFile({ fileId, modelInstanceId });
      return;
    }

    message.error('File not found');
  };

  return (
    <ContentWrapper>
      {label && (
        <FormLabel required={required} htmlFor="dragger">
          {label}
        </FormLabel>
      )}
      <Upload.Dragger
        id="dragger"
        fileList={[]}
        customRequest={({ file }) => setFile(file as File)}
        accept={
          accept ? accept : 'image/*, .pdf, .doc, .docx, .xls, .xlsx, .zip'
        }
        showUploadList={false}
        multiple={false}
        disabled={!!uploadingData || mode === ChunkFileUploadMode.View}
        style={{ marginBottom: '0' }}
      >
        <FileInfoWrapper>
          <FileAddOutlined />
          <Typography.Text>
            {placeholder ? placeholder : 'Upload File'}
          </Typography.Text>
        </FileInfoWrapper>
      </Upload.Dragger>

      {uploadingData && (
        <DragUploadFileList>
          <DragUploadFileListItem
            key={uploadingData.localId}
            file={uploadingData}
            mode={mode}
            onDelete={(file: IUploadingItem) => {
              handleDeleteFile(file);
            }}
            progress={progress?.percentage}
            onRetry={handleRetry}
            onDownload={handleDownload}
            retryTooltip={retryTooltip}
            downloadTooltip={downloadTooltip}
            deleteTooltip={deleteTooltip}
          />
        </DragUploadFileList>
      )}
      {!uploadingData && mode === ChunkFileUploadMode.View && (
        <DangerInfo color={required ? 'red' : 'blue'}>
          <label>File not found</label>
        </DangerInfo>
      )}
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FileInfoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  .anticon {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    margin-right: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.primary};
  }
  .ant-typography {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DangerInfo = styled(Tag)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${({ theme }) => theme.spacing.lg};
  width: max-content;
  padding: 0 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const DragUploadFileList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

export default ChunkFileUpload;
